const STORAGE_KEY = "vle-device-verify-attempt-state";

/**
 * Persist the same map to sessionStorage so lockout survives refresh in-tab even if
 * localStorage was cleared elsewhere; merge on read so both sources stay consistent.
 */
function readAllMaps(): Record<string, unknown> {
  let fromLocal: Record<string, unknown> = {};
  let fromSession: Record<string, unknown> = {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (parsed && typeof parsed === "object") fromLocal = parsed;
    }
  } catch {
    // ignore
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (parsed && typeof parsed === "object") fromSession = parsed;
    }
  } catch {
    // ignore
  }
  return { ...fromSession, ...fromLocal };
}

function writeAllMaps(all: Record<string, unknown>): void {
  const serialized = JSON.stringify(all);
  try {
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch {
    // ignore quota / private mode
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, serialized);
  } catch {
    // ignore
  }
}

/** Per-field verify limit: 3 attempts then 120s lock. */
const MAX_ATTEMPTS_BEFORE_LOCK = 3;
const LOCKOUT_MS = 120_000;

export type DeviceVerifyFieldKey = "sim" | "imei";

type DeviceVerifyFieldAttemptState = {
  attempts: number;
  lockoutUntil: number | null;
};

export type ImeiVerificationAttemptState = {
  attempts: number;
  lockoutUntil: number | null;
};

type DeviceVerificationAttemptState = {
  sim: DeviceVerifyFieldAttemptState;
  imei: DeviceVerifyFieldAttemptState;
};

export function imeiScopeKeySingle(): string {
  return "single-network";
}

export function imeiScopeKeyMulti(cardIndex: number): string {
  return `multi-network-${cardIndex}`;
}

function emptyFieldState(): DeviceVerifyFieldAttemptState {
  return { attempts: 0, lockoutUntil: null };
}

function emptyScopeState(): DeviceVerificationAttemptState {
  return { sim: emptyFieldState(), imei: emptyFieldState() };
}

function normalizeLegacyField(
  raw: unknown
): DeviceVerifyFieldAttemptState {
  if (!raw || typeof raw !== "object") {
    return emptyFieldState();
  }
  const rec = raw as Record<string, unknown>;
  const attemptsFromAttempts =
    typeof rec.attempts === "number" && rec.attempts >= 0
      ? Math.floor(rec.attempts)
      : null;
  const attemptsFromFailures =
    typeof rec.failures === "number" && rec.failures >= 0
      ? Math.floor(rec.failures)
      : null;
  const attempts = attemptsFromAttempts ?? attemptsFromFailures ?? 0;
  const lockoutUntil =
    typeof rec.lockoutUntil === "number" ? rec.lockoutUntil : null;
  return { attempts, lockoutUntil };
}

function normalizeScope(raw: unknown): DeviceVerificationAttemptState {
  if (!raw || typeof raw !== "object") {
    return emptyScopeState();
  }
  const rec = raw as Record<string, unknown>;
  if ("sim" in rec || "imei" in rec) {
    return {
      sim: normalizeLegacyField(rec.sim),
      imei: normalizeLegacyField(rec.imei),
    };
  }
  // Legacy scope shape (IMEI-only): `{ failures, lockoutUntil }`.
  return {
    sim: emptyFieldState(),
    imei: normalizeLegacyField(rec),
  };
}

function getScopeAttemptState(scopeKey: string): DeviceVerificationAttemptState {
  const all = readAllMaps();
  return normalizeScope(all[scopeKey]);
}

function setScopeAttemptState(
  scopeKey: string,
  state: DeviceVerificationAttemptState
): void {
  const all = readAllMaps();
  all[scopeKey] = state;
  writeAllMaps(all);
}

function getFieldAttemptState(
  scopeKey: string,
  field: DeviceVerifyFieldKey
): DeviceVerifyFieldAttemptState {
  const scopeState = getScopeAttemptState(scopeKey);
  const next = resolveExpiredFieldLock(scopeState[field]);
  if (next.attempts !== scopeState[field].attempts || next.lockoutUntil !== scopeState[field].lockoutUntil) {
    setScopeAttemptState(scopeKey, {
      ...scopeState,
      [field]: next,
    });
  }
  return next;
}

function clearFieldAttemptState(
  scopeKey: string,
  field: DeviceVerifyFieldKey
): void {
  const scopeState = getScopeAttemptState(scopeKey);
  setScopeAttemptState(scopeKey, {
    ...scopeState,
    [field]: emptyFieldState(),
  });
}

function recordFieldVerificationAttempt(
  scopeKey: string,
  field: DeviceVerifyFieldKey
): DeviceVerifyFieldAttemptState {
  const current = getFieldAttemptState(scopeKey, field);
  if (current.lockoutUntil != null && Date.now() < current.lockoutUntil) {
    return current;
  }

  const attempts = current.attempts + 1;
  const lockoutUntil =
    attempts >= MAX_ATTEMPTS_BEFORE_LOCK ? Date.now() + LOCKOUT_MS : null;
  const next: DeviceVerifyFieldAttemptState = { attempts, lockoutUntil };
  const scopeState = getScopeAttemptState(scopeKey);
  setScopeAttemptState(scopeKey, {
    ...scopeState,
    [field]: next,
  });
  return next;
}

function clearScopeAttemptState(scopeKey: string): void {
  const all = readAllMaps();
  delete all[scopeKey];
  writeAllMaps(all);
}

function resolveExpiredFieldLock(
  state: DeviceVerifyFieldAttemptState
): DeviceVerifyFieldAttemptState {
  if (state.lockoutUntil != null && Date.now() >= state.lockoutUntil) {
    return emptyFieldState();
  }
  return state;
}

/** Drop expired lockout when reading for UI / submit gate. */

/** RTK unwrap / API error → user-visible string. */
export function parseImeiVerifyErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  const e = error as { data?: unknown; message?: string };
  if (e?.data !== undefined) {
    const d = e.data;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && "message" in d) {
      const m = (d as { message?: string }).message;
      if (typeof m === "string" && m.trim()) return m;
    }
  }
  if (e?.message && typeof e.message === "string") return e.message;
  return "IMEI verification failed.";
}

export function formatImeiLockoutCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function getActiveImeiAttemptState(
  scopeKey: string
): ImeiVerificationAttemptState {
  return getFieldAttemptState(scopeKey, "imei");
}

export function getActiveSimAttemptState(
  scopeKey: string
): ImeiVerificationAttemptState {
  return getFieldAttemptState(scopeKey, "sim");
}

export function clearImeiAttemptState(scopeKey: string): void {
  clearFieldAttemptState(scopeKey, "imei");
}

export function clearSimAttemptState(scopeKey: string): void {
  clearFieldAttemptState(scopeKey, "sim");
}

export function clearAllDeviceAttemptState(scopeKey: string): void {
  clearScopeAttemptState(scopeKey);
}

export function recordImeiVerificationAttempt(
  scopeKey: string
): ImeiVerificationAttemptState {
  return recordFieldVerificationAttempt(scopeKey, "imei");
}

export function recordSimVerificationAttempt(
  scopeKey: string
): ImeiVerificationAttemptState {
  return recordFieldVerificationAttempt(scopeKey, "sim");
}
